/**
 * Frontend runtime for P+R Animations (GSAP + ScrollTrigger).
 *
 * Reads the existing `data-aos-*` attributes from the markup, so content saved
 * with earlier plugin versions keeps working without re-saving any post.
 *
 * Global options (optional), set before this script runs:
 *
 * window.prAnimationsSettings = {
 *     disable: false,               // boolean or function returning boolean
 *     offset: 120,                  // px, default trigger offset
 *     delay: 0,                     // ms
 *     duration: 400,                // ms
 *     easing: 'ease',               // AOS easing name (mapped to GSAP eases)
 *     once: false,
 *     mirror: false,
 *     anchorPlacement: 'top-bottom',
 *     distance: 100,                // px, travel distance for fade/zoom directions
 * };
 *
 * `window.prAnimationsAosSettings` is still read as a fallback.
 *
 * Lenis options (optional):
 * window.prAnimationsLenisSettings = { duration: 1.2, ... };
 * When GSAP is present, Lenis is driven by gsap.ticker and `autoRaf` is forced to false.
 * Lenis is started automatically only if the plugin setting enables it
 * (PHP sets window.prAnimationsConfig.lenis). Blocks can request it via ensureLenis().
 *
 * Public API (window.prAnimations):
 * - presets:               Map of animation name => function(distance) returning GSAP "from" vars.
 * - init(container):       Initialize animations inside a container (e.g. after AJAX loading).
 * - refresh():             Recalculate all trigger positions (ScrollTrigger.refresh()).
 * - ensureLenis(options):  Return the shared Lenis instance, creating it if needed (only one per page).
 * - lenis:                 The shared Lenis instance, if Lenis is active.
 *
 * Elements inside [data-pr-animations-ignore] are skipped (e.g. blocks that animate their children themselves).
 *
 * Event: `pr-animations:ready` is dispatched on document after initialization.
 *
 * @see https://gsap.com/docs/v3/Plugins/ScrollTrigger/
 */

(function () {
	'use strict';

	const root = document.documentElement;
	const READY_CLASS = 'pr-animations-ready';
	const INIT_ATTRIBUTE = 'data-pr-animations-initialized';
	const IGNORE_ATTRIBUTE = 'data-pr-animations-ignore';
	const config = window.prAnimationsConfig || {};
	const REDUCED_MOTION_OK = '(prefers-reduced-motion: no-preference)';

	const DEFAULTS = {
		disable: false,
		offset: 120,
		delay: 0,
		duration: 400,
		easing: 'ease',
		once: false,
		mirror: false,
		anchorPlacement: 'top-bottom',
		distance: 100,
	};

	const settings = {
		...DEFAULTS,
		...(window.prAnimationsAosSettings || {}),
		...(window.prAnimationsSettings || {}),
	};

	/**
	 * AOS easing names mapped to GSAP eases.
	 */
	const EASINGS = {
		linear: 'none',
		ease: 'power1.out',
		'ease-in': 'power1.in',
		'ease-out': 'power1.out',
		'ease-in-out': 'power1.inOut',
		'ease-in-back': 'back.in',
		'ease-out-back': 'back.out',
		'ease-in-out-back': 'back.inOut',
		'ease-in-sine': 'sine.in',
		'ease-out-sine': 'sine.out',
		'ease-in-out-sine': 'sine.inOut',
		'ease-in-quad': 'power1.in',
		'ease-out-quad': 'power1.out',
		'ease-in-out-quad': 'power1.inOut',
		'ease-in-cubic': 'power2.in',
		'ease-out-cubic': 'power2.out',
		'ease-in-out-cubic': 'power2.inOut',
		'ease-in-quart': 'power3.in',
		'ease-out-quart': 'power3.out',
		'ease-in-out-quart': 'power3.inOut',
	};

	/**
	 * Start states ("from" vars) for all animations, matching the AOS look.
	 * Each preset receives the travel distance in px.
	 */
	const presets = {
		fade: () => ({ autoAlpha: 0 }),
		'fade-up': d => ({ autoAlpha: 0, y: d }),
		'fade-down': d => ({ autoAlpha: 0, y: -d }),
		'fade-left': d => ({ autoAlpha: 0, x: d }),
		'fade-right': d => ({ autoAlpha: 0, x: -d }),
		'fade-up-right': d => ({ autoAlpha: 0, x: -d, y: d }),
		'fade-up-left': d => ({ autoAlpha: 0, x: d, y: d }),
		'fade-down-right': d => ({ autoAlpha: 0, x: -d, y: -d }),
		'fade-down-left': d => ({ autoAlpha: 0, x: d, y: -d }),

		'flip-left': () => ({ transformPerspective: 2500, rotationY: -100 }),
		'flip-right': () => ({ transformPerspective: 2500, rotationY: 100 }),
		'flip-up': () => ({ transformPerspective: 2500, rotationX: -100 }),
		'flip-down': () => ({ transformPerspective: 2500, rotationX: 100 }),

		'slide-up': () => ({ yPercent: 100 }),
		'slide-down': () => ({ yPercent: -100 }),
		'slide-left': () => ({ xPercent: 100 }),
		'slide-right': () => ({ xPercent: -100 }),

		'zoom-in': () => ({ autoAlpha: 0, scale: 0.6 }),
		'zoom-in-up': d => ({ autoAlpha: 0, scale: 0.6, y: d }),
		'zoom-in-down': d => ({ autoAlpha: 0, scale: 0.6, y: -d }),
		'zoom-in-left': d => ({ autoAlpha: 0, scale: 0.6, x: d }),
		'zoom-in-right': d => ({ autoAlpha: 0, scale: 0.6, x: -d }),
		'zoom-out': () => ({ autoAlpha: 0, scale: 1.2 }),
		'zoom-out-up': d => ({ autoAlpha: 0, scale: 1.2, y: d }),
		'zoom-out-down': d => ({ autoAlpha: 0, scale: 1.2, y: -d }),
		'zoom-out-left': d => ({ autoAlpha: 0, scale: 1.2, x: d }),
		'zoom-out-right': d => ({ autoAlpha: 0, scale: 1.2, x: -d }),

		'scale-in': () => ({ autoAlpha: 0, scale: 1.03 }),
		'scale-in-x': () => ({ autoAlpha: 0, scaleX: 1.03 }),
		'scale-in-y': () => ({ autoAlpha: 0, scaleY: 1.03 }),
		'scale-out': () => ({ autoAlpha: 0, scale: 0.97 }),
		'scale-out-x': () => ({ autoAlpha: 0, scaleX: 0.97 }),
		'scale-out-y': () => ({ autoAlpha: 0, scaleY: 0.97 }),
	};

	/**
	 * Neutral end values for every property a preset may animate.
	 */
	const NEUTRAL = {
		autoAlpha: 1,
		x: 0,
		y: 0,
		xPercent: 0,
		yPercent: 0,
		rotationX: 0,
		rotationY: 0,
		scale: 1,
		scaleX: 1,
		scaleY: 1,
	};

	/** Properties that are set once instead of being tweened. */
	const STATIC_PROPS = ['transformPerspective'];

	const toNumber = (value, fallback) => {
		const number = parseFloat(value);
		return Number.isFinite(number) ? number : fallback;
	};

	/**
	 * Convert AOS anchor placement + offset into a ScrollTrigger start value.
	 *
	 * AOS "top-bottom" with offset 120 triggers when the element's top is
	 * 120px above the viewport's bottom edge => "top bottom-=120".
	 *
	 * @param {string} anchorPlacement AOS anchor placement, e.g. "center-bottom".
	 * @param {number} offset          Offset in px.
	 * @return {string} ScrollTrigger start.
	 */
	const getStart = (anchorPlacement, offset) => {
		const [element = 'top', viewport = 'bottom'] = String(anchorPlacement).split('-');
		const operator = offset >= 0 ? '-=' : '+=';
		return `${element} ${viewport}${operator}${Math.abs(offset)}`;
	};

	/**
	 * Read the animation config of an element from its data attributes.
	 *
	 * @param {HTMLElement} element Animated element.
	 * @return {Object} Config.
	 */
	const readConfig = element => {
		const { dataset } = element;
		const readBoolean = (value, fallback) => (value === undefined ? fallback : value !== 'false');

		return {
			name: dataset.aos,
			delay: toNumber(dataset.aosDelay, settings.delay),
			duration: toNumber(dataset.aosDuration, settings.duration),
			easing: dataset.aosEasing || settings.easing,
			offset: toNumber(dataset.aosOffset, settings.offset),
			anchorPlacement: dataset.aosAnchorPlacement || settings.anchorPlacement,
			once: readBoolean(dataset.aosOnce, settings.once),
			mirror: readBoolean(dataset.aosMirror, settings.mirror),
			anchor: dataset.aosAnchor ? document.querySelector(dataset.aosAnchor) : null,
		};
	};

	/**
	 * Create the tween + ScrollTrigger for a single element.
	 *
	 * @param {HTMLElement} element Animated element.
	 */
	const animateElement = element => {
		const { gsap } = window;
		const elementConfig = readConfig(element);
		const preset = presets[elementConfig.name];

		if (!preset) {
			return;
		}

		const fromVars = { ...preset(settings.distance) };
		const staticVars = {};
		STATIC_PROPS.forEach(prop => {
			if (prop in fromVars) {
				staticVars[prop] = fromVars[prop];
				delete fromVars[prop];
			}
		});

		const toVars = {};
		Object.keys(fromVars).forEach(prop => {
			toVars[prop] = NEUTRAL[prop];
		});

		if (Object.keys(staticVars).length > 0) {
			gsap.set(element, staticVars);
		}

		/**
		 * toggleActions: onEnter onLeave onEnterBack onLeaveBack
		 * "play" on leave ensures elements that are already scrolled past on page load
		 * (reload, anchor links) are shown, like AOS does.
		 */
		let toggleActions = 'play play none reverse';
		if (elementConfig.mirror) {
			toggleActions = 'play reverse play reverse';
		} else if (elementConfig.once) {
			toggleActions = 'play play none none';
		}

		gsap.fromTo(element, fromVars, {
			...toVars,
			duration: elementConfig.duration / 1000,
			delay: elementConfig.delay / 1000,
			ease: EASINGS[elementConfig.easing] || elementConfig.easing,
			overwrite: 'auto',
			scrollTrigger: {
				trigger: elementConfig.anchor || element,
				start: getStart(elementConfig.anchorPlacement, elementConfig.offset),
				end: 'bottom top',
				toggleActions,
				once: elementConfig.once && !elementConfig.mirror,
			},
		});

		element.setAttribute(INIT_ATTRIBUTE, '');
	};

	/**
	 * Initialize all not yet initialized animated elements inside a container.
	 *
	 * @param {Document|Element} [container=document] Container to search in.
	 */
	const init = (container = document) => {
		if (!window.gsap || !window.ScrollTrigger) {
			return;
		}

		container.querySelectorAll(`[data-aos]:not([${INIT_ATTRIBUTE}])`).forEach(element => {
			if (!element.closest(`[${IGNORE_ATTRIBUTE}]`)) {
				animateElement(element);
			}
		});
		window.ScrollTrigger.refresh();
	};

	const isDisabled = () => {
		const { disable } = settings;
		return typeof disable === 'function' ? !!disable() : !!disable;
	};

	/**
	 * Return the shared Lenis instance, creating it if needed.
	 *
	 * Only one Lenis instance may exist per page. The first caller decides the options;
	 * later callers get the existing instance.
	 *
	 * @param {Object} [options={}] Lenis options (merged over window.prAnimationsLenisSettings).
	 * @return {Object|null} Lenis instance or null if Lenis is not loaded.
	 */
	const ensureLenis = (options = {}) => {
		if (window.prAnimationsLenis) {
			return window.prAnimationsLenis;
		}

		if (typeof window.Lenis === 'undefined') {
			return null;
		}

		const { gsap, ScrollTrigger } = window;
		const useGsapTicker = !!(gsap && ScrollTrigger);
		const lenisSettings = {
			autoRaf: true,
			...(window.prAnimationsLenisSettings || {}),
			...options,
		};

		if (useGsapTicker) {
			lenisSettings.autoRaf = false;
		}

		try {
			const lenis = new window.Lenis(lenisSettings);

			if (useGsapTicker) {
				gsap.registerPlugin(ScrollTrigger);
				lenis.on('scroll', ScrollTrigger.update);
				gsap.ticker.add(time => lenis.raf(time * 1000));
				gsap.ticker.lagSmoothing(0);
			}

			window.prAnimationsLenis = lenis;
			window.prAnimations.lenis = lenis;
			if (!window.lenis) {
				window.lenis = lenis;
			}

			return lenis;
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('P+R Animations: Failed to initialize Lenis:', error);
			return null;
		}
	};

	const markReady = () => {
		root.classList.add(READY_CLASS);
		document.dispatchEvent(new CustomEvent('pr-animations:ready', { detail: window.prAnimations }));
	};

	const initializeFrontend = () => {
		const { gsap, ScrollTrigger } = window;

		if (config.lenis) {
			ensureLenis();
		}

		if (!gsap || !ScrollTrigger) {
			if (document.querySelector('[data-aos]')) {
				// eslint-disable-next-line no-console
				console.warn('P+R Animations: GSAP or ScrollTrigger not found. Animations are disabled.');
			}
			markReady();
			return;
		}

		gsap.registerPlugin(ScrollTrigger);

		if (isDisabled()) {
			markReady();
			return;
		}

		/**
		 * Only animate when the user has no reduced-motion preference.
		 * gsap.matchMedia() reverts all animations automatically if the preference changes.
		 */
		gsap.matchMedia().add(REDUCED_MOTION_OK, () => {
			init(document);

			return () => {
				document.querySelectorAll(`[${INIT_ATTRIBUTE}]`).forEach(element => {
					element.removeAttribute(INIT_ATTRIBUTE);
				});
			};
		});

		markReady();
	};

	window.prAnimations = {
		presets,
		init: container => {
			if (isDisabled() || !window.matchMedia(REDUCED_MOTION_OK).matches) {
				return;
			}
			init(container);
		},
		refresh: () => window.ScrollTrigger && window.ScrollTrigger.refresh(),
		ensureLenis: options => ensureLenis(options),
		lenis: null,
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initializeFrontend);
	} else {
		initializeFrontend();
	}
})();
